import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useTranslation } from "@shared/utils/internationalization";
import BackButton from "@ui-application/form/BackButton/BackButton.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useState } from "react";
import GuestCheckoutForm from "../GuestCheckoutForm/GuestCheckoutForm.web";
import type { GuestCheckoutSharedProps } from "./GuestCheckout.props";

export type GuestCheckoutProps = GuestCheckoutSharedProps & {
  // Add any web specific props here.
};

export default function GuestCheckout(props: GuestCheckoutProps): JSX.Element {
  const { onBackClick, onConfirmClick, isLoading } = props;
  const { t } = useTranslation("common");

  const {
    isOpen: isGuestCheckoutModalOpen,
    onOpen: onGuestCheckoutModalOpen,
    onClose: onGuestCheckoutModalClose,
  } = useDisclosure();

  const [email, setEmail] = useState<string>("");

  const [customerData, setCustomerData] = useState<{
    name: string;
    email: string;
    phone: string;
  }>({ name: "", email: "", phone: "" });

  const handleContinueClick = (data: {
    name: string;
    email: string;
    phone: string;
  }) => {
    setEmail(data.email);
    setCustomerData(data);
    onGuestCheckoutModalOpen();
  };

  return (
    <Box w="100%" maxW="sm">
      {onBackClick && (
        <Box paddingBottom={{ base: "lg", sm: "3xl" }}>
          <BackButton
            data-testid="guest-checkout-back"
            onClick={onBackClick}
            label={t("Back to login")}
          />
        </Box>
      )}
      <Text
        fontSize="40px"
        fontWeight="bold"
        paddingBottom="xl"
        lineHeight="40px"
      >
        {t("Where should we send your order?")}
      </Text>
      <Text fontSize="md" color="gray.600" paddingBottom="xl">
        {t("Order will be delivered to the email address provided below")}
      </Text>
      <GuestCheckoutForm
        onContinueClick={handleContinueClick}
        isLoading={isLoading}
      />
      <Modal
        isOpen={isGuestCheckoutModalOpen}
        onClose={onGuestCheckoutModalClose}
        isCentered
      >
        <Modal.Body>
          <Flex direction="column" alignItems="center" gap="md">
            <Icon.At.Solid boxSize="element.5xl" colorPalette="primary" />
            <Text fontSize="sm">
              {t("Please confirm the email address below is correct")}
            </Text>
            <Text
              fontWeight="bold"
              fontSize="md"
              wordBreak="break-word"
              maxW="100%"
            >
              {email}
            </Text>
            <Button
              w="100%"
              variant="solid"
              onClick={() => {
                onConfirmClick(customerData);
                onGuestCheckoutModalClose();
              }}
              data-testid="confirm-email-button"
            >
              {t("Confirm")}
            </Button>
          </Flex>
          <Modal.CloseButton color="fg" />
        </Modal.Body>
      </Modal>
    </Box>
  );
}
